import { Box, Chip, Paper, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import React, { FC, useEffect, useMemo } from 'react';
import { Can } from '../../../Components/Can';
import { Flex } from '../../../Components/Flex/flex.component';
import { KanbanBoardProduction } from '../../../Components/KanbanBoardProduction/kanban-board.component';
import { TableListSkeleton } from '../../../Components/Table/table-list-skeleton.component';
import { Placeholder } from '../../../Components/Placeholder/placeholder.component';
import { Table } from '../../../Components/Table/table.component';
import { CustomTabPanel } from '../../../Components/Tabs/CustomTabPanel/custom-tab-panel.component';
import { Tag } from '../../../Components/Tag/tag.component';
import { BaseContentPage } from '../../../Components/Templates/BaseContentPage/base-content-page.componen';
import { BaseScreen } from '../../../Components/Templates/BaseScreen/basescreen.component';
import { TABLE_STICKY_MAX_HEIGHT } from '../../../Styles/table-layout';
import { ServiceOrderDueDateStatusEnum, ServiceOrderStageEnum, ServiceOrderStatusEnum } from '../../../Services/api/service-orders/service-order.interface';
import { ListServiceOrdersUseCase } from '../../../Usecase/ServiceOrders/list-service-orders-usecase';
import { ShowServiceOrdersUseCase } from '../../../Usecase/ServiceOrders/show-service-orders-usecase';
import { UpdateServiceOrdersUseCase } from '../../../Usecase/ServiceOrders/update-service-orders-usecase';
import { a11yProps } from '../../../utils/a11yProps';
import { labelsOrderProductionStage, normalizeOrderProductionStage } from '../../../utils/normalizeProductionStages';
import { labelsOrderProductionStatus, normalizeOrderProductionStatus } from '../../../utils/normalizeProductionStatus';
import { labelsTimeStatusProduction, normalizeTimeStatusColors } from '../../../utils/normalizeTimeStatus';
import { useListProduction } from './production-list.hook';

interface IProductionList {
    listServiceOrdersUseCase : ListServiceOrdersUseCase
    showServiceOrdersUseCase: ShowServiceOrdersUseCase
    updateServiceOrdersUseCase: UpdateServiceOrdersUseCase
}

const tagSx = { fontSize: '0.75rem' } as const;

const ProductionList : FC<IProductionList> = ({  listServiceOrdersUseCase, showServiceOrdersUseCase, updateServiceOrdersUseCase }) => {
    const { listProduction, list, handleConfirmProductionItems, kanbanSchema ,handleShowOrderDetails, orderDetails, setOrderDetails, tabValue,
      handleChangeTab, setProductionTypeView, productionTypeView, totalRegisters, handleNavigateDetails, loading, loadError, page, handlePageChange } = useListProduction({
      listServiceOrdersUseCase,
      showServiceOrdersUseCase,
      updateServiceOrdersUseCase
    })

    useEffect(() => {
        listProduction({ page: 1 })
    }, [listProduction])

    const columns = useMemo(() => {
      const createColumns: any[] = [
        {
          field: 'externalProductionId',
          headerName: 'Ordem de Produção',
          minWidth: 150,
          width: 170,
          sortable: false,
          renderCell: (params: any) => (
            <Typography variant="body2" fontWeight={600} color="primary.main">
              {params?.row?.externalProductionId ? `#${params.row.externalProductionId}` : 'Sem OP'}
            </Typography>
          ),
        },
        {
          field: 'productsCount',
          headerName: 'Produtos',
          width: 100,
          align: 'center',
          headerAlign: 'center',
          sortable: false,
          renderCell: (params: any) => (
            <Chip
              size="small"
              label={params.value === 1 ? '1 produto' : `${params.value ?? 0} produtos`}
              variant="outlined"
            />
          ),
        },
        {
          field: 'delayedProductsCount',
          headerName: 'Atrasados',
          width: 100,
          align: 'center',
          headerAlign: 'center',
          sortable: false,
          renderCell: (params: any) => (
            <Chip
              size="small"
              label={params.value ?? 0}
              color={(params.value ?? 0) > 0 ? 'error' : 'default'}
              variant={(params.value ?? 0) > 0 ? 'filled' : 'outlined'}
            />
          ),
        },
        {
          field: 'primaryProductName',
          headerName: 'Produto',
          minWidth: 220,
          flex: 1.5,
          sortable: false,
          renderCell: (params: any) => {
            const products = params.row?.products ?? [];
            const summary = products.length === 0
              ? 'Não informado'
              : products.length === 1
                ? products[0]?.product?.name
                : `${products[0]?.product?.name} +${products.length - 1}`;

            return (
              <Tooltip title={products.map((p: any) => p?.product?.name).filter(Boolean).join('\n')} placement="top-start">
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.35,
                  }}
                >
                  {summary}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          field: "status",
          headerName: "Status",
          width: 140,
          sortable: false,
          renderCell: (params: any) => (
            <Tag
              title={labelsOrderProductionStatus[params.row.status as ServiceOrderStatusEnum] || 'Desconhecido'}
              boxStyle={{ backgroundColor: normalizeOrderProductionStatus(params.row.status as ServiceOrderStatusEnum)?.bgColor }}
              titleStyle={{ color: normalizeOrderProductionStatus(params.row.status as ServiceOrderStatusEnum)?.textColor, ...tagSx }}
              wrapperStyle={{ width: '100%' }}
            />
          ),
        },
        {
          field: "stages",
          headerName: "Etapas",
          minWidth: 200,
          flex: 1,
          sortable: false,
          renderCell: (params: any) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5, width: '100%' }}>
              {(params.row.stages ?? []).map((item: ServiceOrderStageEnum) => (
                <Tag
                  key={item}
                  title={labelsOrderProductionStage[item] || 'Desconhecido'}
                  boxStyle={{ backgroundColor: normalizeOrderProductionStage(item)?.bgColor }}
                  titleStyle={{ color: normalizeOrderProductionStage(item)?.textColor, ...tagSx }}
                />
              ))}
            </Box>
          ),
        },
      ];

      return createColumns;
    }, []);

    return (
      <Can permissions={['menu:production:manage']}>  
        <BaseScreen withSideBar={false}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>

                <BaseContentPage
                    title="Produção"
                    subtitle="Acompanhe ordens de produção, status e etapas"
                    minHeight="auto"
                    breadcrumb={[
                      { title: 'Produção', link: "/production-list" },
                      { title: 'Listagem' },
                    ]}
                    Action={() => (
                      <Tabs value={tabValue} onChange={handleChangeTab} aria-label="Abas de produção">
                        <Tab label="Ordem de Produção" {...a11yProps(0)} />
                      </Tabs>
                    )}
                >

                <CustomTabPanel value={tabValue} index={0}>
                  {productionTypeView === '0' ? (
                    !!list?.length ? (
                      <Box sx={{ width: '100%', minWidth: 0 }}>
                        <Table
                          onRowClick={(row) => handleNavigateDetails(row)}
                          isRowSelectable={() => false}
                          data={list}
                          columns={columns}
                          page={page}
                          onPageChange={handlePageChange}
                          totalCount={totalRegisters}
                          disableRowSelectionOnClick={false}
                          rowHeight={72}
                          stickyHeader
                          maxHeight={TABLE_STICKY_MAX_HEIGHT}
                          sx={{ width: '100%' }}
                        />
                      </Box>
                    ) : loading ? (
                      <TableListSkeleton rowHeight={72} />
                    ) : loadError ? (
                      <Placeholder
                        description="Não foi possível carregar as ordens de produção."
                        onRetry={() => listProduction({ page })}
                      />
                    ) : (
                      <Placeholder description="Nenhuma ordem de produção encontrada." />
                    )
                  ) : (
                    !!kanbanSchema?.columnOrder?.length ? (
                      <KanbanBoardProduction
                        handleShowOrderDetails={handleShowOrderDetails}
                        handleConfirmProductionItems={handleConfirmProductionItems}
                        initialData={kanbanSchema}
                        details={orderDetails}
                        setOrderDetails={setOrderDetails}
                        canClick={true}
                        canDrag={true}
                      />
                    ) : loading ? (
                      <TableListSkeleton rowHeight={72} />
                    ) : loadError ? (
                      <Placeholder
                        description="Não foi possível carregar as ordens de produção."
                        onRetry={() => listProduction({ page })}
                      />
                    ) : (
                      <Placeholder description="Nenhuma ordem de produção encontrada." />
                    )
                  )}
                </CustomTabPanel>

                </BaseContentPage>
              
            </Paper>
                
        </BaseScreen>
        </Can>
      )
}

export { ProductionList };
